/**
 * EmailTemplateDiffModal — modal che mostra il diff line-by-line tra
 * il testo di default della piattaforma e l'override salvato da un
 * admin, per ogni field editabile di un template email.
 *
 * Aperto dalla EmailTemplateOverviewPage cliccando su una cella
 * "customized" (rossa). Read-only: NON modifica nulla. Consente
 * un "Reset default" per rimuovere l'override e tornare al testo
 * di piattaforma.
 *
 * Rendering diff: colonna sinistra = default, colonna destra =
 * override. Le righe hanno un ``tag`` (same/change/add/remove) che
 * determina il colore.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, RotateCcw, CheckCircle2, FileEdit, User,
  Clock, ArrowRight, ExternalLink,
} from "lucide-react";
import { API } from "../../config";

export const EmailTemplateDiffModal = ({ emailType, country, onClose, onReset }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/email-templates/${emailType}/diff?country=${country}`,
      );
      setData(r.data);
    } catch (e) {
      toast.error("Impossibile caricare il diff");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [emailType, country, onClose]);

  useEffect(() => { load(); }, [load]);

  const performReset = async () => {
    setResetting(true);
    try {
      await axios.delete(
        `${API}/admin/email-templates/${emailType}?country=${country}`,
      );
      toast.success(`Override rimosso: ${emailType}/${country} torna al default di piattaforma`);
      onReset?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fallito");
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="email-template-diff-modal"
    >
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="border-b border-gray-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <code className="text-[10px] font-mono text-lojack-blue-400 bg-lojack-blue-500/10 px-1.5 py-0.5 rounded">
                {emailType}
              </code>
              <ArrowRight size={11} className="text-gray-600" />
              <code className="text-[10px] font-mono text-lojack-blue-400 bg-lojack-blue-500/10 px-1.5 py-0.5 rounded">
                {country}
              </code>
            </div>
            <h2 className="text-lg font-semibold text-white truncate">
              {data?.email_type_label || "Caricamento…"}
            </h2>
            {data?.updated_by && (
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <User size={10} /> {data.updated_by}
                </span>
                {data.updated_at && (
                  <span className="flex items-center gap-1">
                    <Clock size={10} /> {new Date(data.updated_at).toLocaleString("it-IT")}
                  </span>
                )}
                <span className="flex items-center gap-1 text-lojack-blue-400">
                  <FileEdit size={10} /> {data.changed_field_count}/{data.total_field_count} campi custom
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition p-2 rounded-lg hover:bg-gray-900 flex-shrink-0"
            data-testid="diff-modal-close-btn"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="diff-modal-body">
          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 size={24} className="animate-spin text-gray-600" />
            </div>
          )}

          {!loading && data && !data.has_override && (
            <div className="text-center py-12 text-gray-400" data-testid="diff-no-override">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-emerald-500" />
              <p className="text-sm">Nessun override attivo per <code className="text-white">{emailType}/{country}</code>.</p>
              <p className="text-xs text-gray-500 mt-1">
                Il testo di default della piattaforma è usato integralmente.
              </p>
            </div>
          )}

          {!loading && data?.has_override && (
            <div className="space-y-4">
              {/* Header sopra la lista campi */}
              <div className="grid grid-cols-2 gap-4 pb-2 border-b border-gray-800">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Default di piattaforma
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-lojack-blue-400">
                  Override attivo
                </div>
              </div>

              {data.fields.map((f) => (
                <FieldDiff key={f.key} field={f} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <a
            href={`/admin/email-preview?type=${emailType}&country=${country}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-lojack-blue-400 hover:text-lojack-blue-300 flex items-center gap-1.5"
            data-testid="diff-modal-open-editor-btn"
          >
            <ExternalLink size={11} />
            Apri nell&apos;editor Email Preview
          </a>
          <div className="ml-auto flex items-center gap-2">
            {data?.has_override && !confirmReset && (
              <button
                onClick={() => setConfirmReset(true)}
                className="text-xs px-4 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition flex items-center gap-1.5"
                data-testid="diff-modal-reset-btn"
              >
                <RotateCcw size={12} />
                Rimuovi override
              </button>
            )}
            {confirmReset && (
              <>
                <span className="text-xs text-rose-300">Confermi il reset?</span>
                <button
                  onClick={performReset}
                  disabled={resetting}
                  className="text-xs px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="diff-modal-reset-confirm-btn"
                >
                  {resetting
                    ? <Loader2 size={12} className="animate-spin" />
                    : <RotateCcw size={12} />}
                  Sì, rimuovi
                </button>
                <button
                  onClick={() => setConfirmReset(false)}
                  disabled={resetting}
                  className="text-xs px-3 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900"
                  data-testid="diff-modal-reset-cancel-btn"
                >
                  Annulla
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-xs px-4 py-2 rounded-lg bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white font-semibold"
              data-testid="diff-modal-done-btn"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FieldDiff = ({ field }) => {
  const isSame = !field.customized;
  return (
    <div
      className={`border rounded-xl p-4 ${
        isSame ? "border-gray-900 bg-[#080808] opacity-60" : "border-lojack-blue-500/20 bg-[#0f1522]"
      }`}
      data-testid={`diff-field-${field.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-semibold text-white">
            {field.label}
          </div>
          <code className="text-[9px] font-mono text-gray-500">{field.key}</code>
        </div>
        {field.customized ? (
          <span className="text-[10px] px-2 py-0.5 rounded bg-lojack-blue-500/20 border border-lojack-blue-500/40 text-lojack-blue-200 font-semibold">
            MODIFICATO
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-gray-500 font-semibold">
            invariato
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Left: default */}
        <div className="bg-[#050505] border border-gray-900 rounded-lg p-3">
          <pre className="text-[11px] leading-relaxed text-gray-300 font-mono whitespace-pre-wrap break-words">
            {field.default || <span className="text-gray-600 italic">(vuoto)</span>}
          </pre>
        </div>
        {/* Right: override (o vuoto se non custom) */}
        <div className={`border rounded-lg p-3 ${
          field.customized ? "bg-emerald-950/20 border-emerald-500/30" : "bg-[#050505] border-gray-900"
        }`}>
          <pre className="text-[11px] leading-relaxed text-gray-300 font-mono whitespace-pre-wrap break-words">
            {field.customized
              ? field.override
              : <span className="text-gray-600 italic">(usa il default)</span>}
          </pre>
        </div>
      </div>

      {/* Line-by-line diff visualization when there's an override */}
      {field.customized && field.diff_lines?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-900">
          <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-1.5">
            Confronto riga-per-riga
          </div>
          <div className="bg-[#050505] border border-gray-900 rounded-lg p-2 space-y-0.5" data-testid={`diff-lines-${field.key}`}>
            {field.diff_lines.map((line, i) => (
              <DiffLine key={i} line={line} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DiffLine = ({ line }) => {
  const tagStyles = {
    same: "text-gray-600",
    change: "text-amber-300 bg-amber-500/5",
    add: "text-emerald-400 bg-emerald-500/5",
    remove: "text-rose-400 bg-rose-500/5 line-through opacity-70",
  };
  const marker = {
    same: " ",
    change: "~",
    add: "+",
    remove: "-",
  };
  return (
    <div className={`flex items-start gap-2 text-[10px] font-mono px-1.5 py-0.5 rounded ${tagStyles[line.tag] || ""}`}>
      <span className="w-3 flex-shrink-0 text-center">{marker[line.tag]}</span>
      <span className="flex-1 whitespace-pre-wrap break-words">
        {line.override ?? line.default ?? ""}
      </span>
    </div>
  );
};

export default EmailTemplateDiffModal;
